/**
 * ProjectHeaderContext — lets the ProjectDetail page push its
 * project doc into the global topbar so the topbar can render
 * the project context (name + subtitle + progress + Share / Edit
 * / avatar) instead of the default search + new + bell row.
 *
 * Same shape as BreadcrumbContext — page sets on mount, clears on
 * unmount. Decoupled both ways:
 *   - The topbar doesn't know which page is rendering.
 *   - The page doesn't know the topbar exists.
 *
 * Type kept loose (`unknown` for `project`) on purpose so this file
 * doesn't pull in `ProjectListItem` types and create a circular
 * import. Consumers do their own narrowing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { ProjectListItem } from '@/lib/useProjects';

type Ctx = {
  /** Project to render in the topbar; `null` falls back to the
   *  default search + new + bell layout. */
  project: ProjectListItem | null;
  setProject: (p: ProjectListItem | null) => void;
};

const ProjectHeaderCtx = createContext<Ctx>({
  project: null,
  setProject: () => {},
});

export function ProjectHeaderProvider({ children }: { children: ReactNode }) {
  const [project, setProject] = useState<ProjectListItem | null>(null);
  const value = useMemo(() => ({ project, setProject }), [project]);
  return (
    <ProjectHeaderCtx.Provider value={value}>
      {children}
    </ProjectHeaderCtx.Provider>
  );
}

/** Read the active project (or null). Used by `TopBar`. */
export function useProjectHeader(): ProjectListItem | null {
  return useContext(ProjectHeaderCtx).project;
}

/** Page helper — call with the project doc on mount; auto-clears
 *  on unmount so the next route doesn't inherit a stale header. */
export function useSetProjectHeader(project: ProjectListItem | null) {
  const { setProject } = useContext(ProjectHeaderCtx);
  const stable = useCallback(setProject, [setProject]);
  useEffect(() => {
    stable(project);
    return () => stable(null);
  }, [project, stable]);
}
